import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, Table, TableBody, TableCell, TableHead, TableRow,
  Typography, Button, TextField, Chip, IconButton, MenuItem, Select,
  InputAdornment, TablePagination, Tooltip, CircularProgress, LinearProgress
} from '@mui/material';
import { Add, Search, Edit, Payment, Print, Cancel, Delete } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { API } from '../context/AuthContext';
import { STATUS_COLORS, STATUS_LABELS, fmt } from '../utils';
import PaymentDialog from '../components/payments/PaymentDialog';
import { printInvoice } from '../components/PrintDocument';
import { useSnackbar } from 'notistack';

const STATUSES = ['', 'borrador', 'enviada', 'parcial', 'pagada', 'vencida', 'cancelada'];

export default function InvoicesPage() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [payDialog, setPayDialog] = useState({ open: false, invoice: null });

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    const { data } = await API.get('/invoices', { params: { search, status, page: page + 1, limit: 15 } });
    setInvoices(data.invoices); setTotal(data.total);
    setLoading(false);
  }, [search, status, page]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handlePrint = async (id) => {
    try {
      const { data } = await API.get(`/invoices/${id}`, { headers: { 'Cache-Control': 'no-cache' } });
      printInvoice(data);
    } catch {
      console.error('Error al cargar factura');
    }
  };

  const cancelInvoice = async (id, number) => {
    if (!window.confirm(`¿Cancelar la factura ${number}? Esta acción no se puede deshacer.`)) return;
    try {
      await API.patch(`/invoices/${id}/status`, { status: 'cancelada' });
      enqueueSnackbar('Factura cancelada', { variant: 'info' });
      fetchInvoices();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || 'Error al cancelar', { variant: 'error' });
    }
  };

  const deleteInvoice = async (id, number) => {
    if (!window.confirm(`¿Eliminar PERMANENTEMENTE la factura ${number}? Esto no se puede deshacer y solo funciona si no tiene pagos aplicados.`)) return;
    try {
      await API.delete(`/invoices/${id}`);
      enqueueSnackbar('Factura eliminada', { variant: 'success' });
      fetchInvoices();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || 'Error al eliminar', { variant: 'error' });
    }
  };

  return (
    <Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800} color="#1E1B2E">Facturas</Typography>
          <Typography variant="body2" color="text.secondary">{total} facturas en total</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/facturas/nueva')}>Nueva factura</Button>
      </Box>

      <Card>
        <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField placeholder="Buscar…" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} sx={{ flex: 1, minWidth: 200 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: 'text.secondary' }} /></InputAdornment> }} />
          <Select value={status} onChange={e => { setStatus(e.target.value); setPage(0); }} displayEmpty size="small" sx={{ minWidth: 160 }}>
            <MenuItem value="">Todos los estados</MenuItem>
            {STATUSES.filter(Boolean).map(s => <MenuItem key={s} value={s}>{STATUS_LABELS[s]}</MenuItem>)}
          </Select>
        </Box>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Número</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell>Vencimiento</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Saldo</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6 }}><CircularProgress size={32} /></TableCell></TableRow>
            ) : invoices.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>Sin facturas</TableCell></TableRow>
            ) : invoices.map(inv => {
              const paidPct = inv.total > 0 ? (inv.paid / inv.total) * 100 : 0;
              const isOverdue = inv.dueDate && new Date(inv.dueDate) < new Date() && !['pagada','cancelada'].includes(inv.status);
              return (
                <TableRow key={inv._id} hover sx={{ background: isOverdue ? 'rgba(239,68,68,0.02)' : undefined }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700} color="primary.main" sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/facturas/${inv._id}/editar`)}>
                      {inv.number}
                    </Typography>
                    {inv.quotes?.length > 0 && (
                      <Typography variant="caption" color="text.secondary">desde {inv.quotes.map(q => q.number).join(', ')}</Typography>
                    )}
                  </TableCell>
                  <TableCell><Typography variant="body2">{fmt.date(inv.date || inv.createdAt)}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: isOverdue ? 'error.main' : undefined, fontWeight: isOverdue ? 700 : 400 }}>
                      {fmt.date(inv.dueDate)}
                    </Typography>
                  </TableCell>
                  <TableCell><Typography variant="body2" fontWeight={500}>{inv.clientSnapshot?.name || inv.client?.name}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight={600}>{fmt.currency(inv.total)}</Typography></TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={700} color={inv.balance > 0 ? 'warning.main' : 'success.main'}>
                      {fmt.currency(inv.balance || 0)}
                    </Typography>
                    {inv.paid > 0 && inv.total > 0 && (
                      <LinearProgress variant="determinate" value={paidPct} sx={{ mt: 0.5, height: 3, borderRadius: 2 }} color={paidPct >= 100 ? 'success' : 'warning'} />
                    )}
                  </TableCell>
                  <TableCell><Chip label={STATUS_LABELS[inv.status]} color={STATUS_COLORS[inv.status]} size="small" /></TableCell>
                  <TableCell align="right">
                    <Tooltip title="Imprimir / PDF">
                      <IconButton size="small" color="primary" onClick={() => handlePrint(inv._id)}>
                        <Print fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => navigate(`/facturas/${inv._id}/editar`)}><Edit fontSize="small" /></IconButton>
                    </Tooltip>
                    {!['pagada','cancelada'].includes(inv.status) && (
                      <Tooltip title="Aplicar pago">
                        <IconButton size="small" color="success" onClick={() => setPayDialog({ open: true, invoice: inv })}>
                          <Payment fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {inv.status !== 'cancelada' && (
                      <Tooltip title="Cancelar factura">
                        <IconButton size="small" color="warning" onClick={() => cancelInvoice(inv._id, inv.number)}>
                          <Cancel fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {inv.paid === 0 && (
                      <Tooltip title="Eliminar permanentemente">
                        <IconButton size="small" color="error" onClick={() => deleteInvoice(inv._id, inv.number)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination component="div" count={total} page={page} rowsPerPage={15}
          onPageChange={(_, p) => setPage(p)} rowsPerPageOptions={[15]}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`} />
      </Card>

      <PaymentDialog open={payDialog.open} invoice={payDialog.invoice}
        onClose={() => setPayDialog({ open: false, invoice: null })}
        onSave={() => { setPayDialog({ open: false, invoice: null }); fetchInvoices(); }} />
    </Box>
  );
}
